import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { HeartHandshake, CheckCircle2, ChevronRight, MessageCircle, Calendar, LineChart, ShieldCheck, HeartPulse, Trophy } from 'lucide-react';
import ProgrammePageShell from './ProgrammePageShell';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };

const SupportFeature = ({ icon: Icon, title, desc }) => (
  <motion.div
    variants={fadeUp}
    className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg p-6 transition-all duration-300 hover:-translate-y-1"
  >
    <div className="w-11 h-11 bg-rose-50 rounded-xl flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-[#800000]" />
    </div>
    <h3 className="font-bold text-slate-900 mb-2 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h3>
    <p className="text-slate-600 text-sm leading-relaxed">{desc}</p>
  </motion.div>
);

const FAQ = ({ q, a }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left font-semibold text-slate-900 hover:bg-slate-50 transition-colors"
        aria-expanded={open}
      >
        <span>{q}</span>
        <ChevronRight className={`w-5 h-5 text-[#800000] flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && <div className="px-6 pb-4 text-slate-600 text-sm leading-relaxed border-t border-slate-100 pt-3">{a}</div>}
    </div>
  );
};

const LongTermSupport = () => {
  const features = [
    { icon: MessageCircle, title: 'Priority Telehealth Access', desc: 'Direct messaging and video consultations with your care coordinator whenever you need guidance or face a challenge.' },
    { icon: Calendar, title: 'Structured Follow-ups', desc: 'Pre-scheduled milestones at 1, 3, 6, 9, 12, 18, and 24 months to formally review metabolic markers and adjust your maintenance plan.' },
    { icon: LineChart, title: 'Continuous Progress Tracking', desc: 'Ongoing monitoring of weight, body composition, and blood work to catch and reverse any early signs of weight regain or metabolic drift.' },
    { icon: HeartPulse, title: 'Psychological Support', desc: 'Access to behavioural counselling for navigating the emotional aspects of massive weight loss, body image changes, and social dynamics.' },
    { icon: ShieldCheck, title: 'Nutritional Surveillance', desc: 'Particularly critical for post-bariatric surgery patients, ensuring lifelong vitamin and mineral adequacy to prevent deficiencies.' },
    { icon: Trophy, title: 'Support Groups & Community', desc: 'Connect with peers who share similar journeys. Shared experiences provide powerful motivation and practical everyday tips.' },
  ];

  const phases = [
    { phase: 'Phase 1', title: 'Intensive Intervention (Months 1-6)', desc: 'High-frequency contact. Weekly or bi-weekly check-ins to establish new habits, monitor surgical recovery or medical response, and ensure rapid, safe weight loss.' },
    { phase: 'Phase 2', title: 'Transition to Maintenance (Months 6-12)', desc: 'As weight stabilises, focus shifts from weight loss to metabolic conditioning and preventing regain. Check-ins transition to monthly.' },
    { phase: 'Phase 3', title: 'Lifelong Sustenance (Year 1+)', desc: 'Quarterly or bi-annual reviews. You are now equipped with the tools to manage your health independently, with our team acting as a safety net.' },
  ];

  const faqs = [
    { q: 'Is long-term support only for surgical patients?', a: 'No. While post-operative monitoring is clinically mandated for surgical patients, our medical weight management patients also receive structured long-term support to ensure lasting results.' },
    { q: 'What happens if I start regaining weight?', a: 'Weight regain is a common biological response, not a personal failure. If tracking shows an upward trend, we intervene early by reviewing your plan, adjusting medications, or providing behavioural support before significant regain occurs.' },
    { q: 'Can I stay in touch with my dietitian after the first year?', a: 'Absolutely. Nutrition is a lifelong journey. You can opt into our maintenance programmes which provide continued access to your dietitian and care coordinator indefinitely.' },
    { q: 'Are support group sessions mandatory?', a: 'They are completely optional, but highly recommended. Our data shows that patients who engage with support groups maintain their weight loss more successfully over a 5-year period.' },
    { q: 'Do you monitor vitamins for life after surgery?', a: 'Yes. Certain bariatric procedures alter nutrient absorption permanently. Annual or bi-annual nutritional blood panels are a non-negotiable part of our long-term commitment to your health.' },
  ];

  return (
    <>
      <Helmet>
        <title>Long-Term Support for Obesity | GastroCare Bhopal</title>
        <meta name="description" content="Discover GastroCare Bhopal's long-term obesity support programme. We provide lifelong follow-ups, nutritional surveillance, and psychological care to prevent weight regain." />
        <meta property="og:title" content="Long-Term Support | GastroCare Bhopal" />
        <meta property="og:description" content="Weight loss is just the beginning. Our structured long-term support ensures your metabolic transformation lasts a lifetime." />
      </Helmet>

      <ProgrammePageShell
        title="Long-Term Support"
        subtitle="Weight loss is an achievement, but maintenance is the true victory. Our relationship with you does not end when you reach your goal weight—it evolves to ensure your results last a lifetime."
        icon={HeartHandshake}
        breadcrumb="Long-Term Support"
      >
        {/* Introduction */}
        <motion.section variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-24 pb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeUp}>
              <span className="text-[#800000] font-semibold text-sm uppercase tracking-wider">Beyond Weight Loss</span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2 mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Protecting Your Investment in Health
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                The biological reality of obesity is that the body aggressively tries to defend its highest weight. After significant weight loss—whether medical or surgical—metabolic rate drops and appetite hormones increase. This is why 80% of people who lose weight on standard diets regain it.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                At GastroCare Bhopal, we treat obesity as a chronic disease requiring chronic management. Our long-term support programme is specifically designed to combat metabolic adaptation and behavioural fatigue.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Through structured clinical monitoring, nutritional surveillance, and psychological support, we provide a permanent safety net. We don't just help you lose the weight; we teach your body and mind how to keep it off forever.
              </p>
            </motion.div>
            <motion.div variants={fadeUp} className="relative">
              <img
                src={process.env.PUBLIC_URL + "/img-2.png"} 
                alt="Doctor smiling and shaking hands with patient"
                className="rounded-2xl w-full h-72 lg:h-96 object-cover shadow-xl"
                loading="lazy"
              />
              <div className="absolute -bottom-4 -left-4 bg-[#800000] text-white rounded-2xl px-5 py-4 shadow-lg hidden md:block">
                <p className="text-2xl font-extrabold leading-none">5 Yrs</p>
                <p className="text-sm opacity-90">Post-Care Protocol</p>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Support Features */}
        <motion.section variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-24">
          <motion.div variants={fadeUp} className="text-center mb-12">
            <span className="text-[#800000] font-semibold text-sm uppercase tracking-wider">The Safety Net</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Comprehensive Post-Care</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
            {features.map((f, i) => <SupportFeature key={i} {...f} />)}
          </div>
        </motion.section>

        {/* Journey Phases */}
        <motion.section variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-24 bg-slate-50 rounded-3xl p-8 lg:p-12">
          <motion.div variants={fadeUp} className="text-center mb-12">
            <span className="text-[#800000] font-semibold text-sm uppercase tracking-wider">The Timeline</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Your Long-Term Journey</h2>
          </motion.div>
          <div className="space-y-4 max-w-4xl mx-auto pt-4">
            {phases.map((phase, i) => (
              <motion.div key={i} variants={fadeUp} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 flex flex-col sm:flex-row gap-6 items-start">
                <div className="bg-rose-50 text-[#800000] font-extrabold px-4 py-2 rounded-lg flex-shrink-0 whitespace-nowrap">
                  {phase.phase}
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-2 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>{phase.title}</h3>
                  <p className="text-slate-600 leading-relaxed text-sm">{phase.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* FAQs */}
        <motion.section variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
          <motion.div variants={fadeUp} className="text-center mb-12">
            <span className="text-[#800000] font-semibold text-sm uppercase tracking-wider">FAQs</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Frequently Asked Questions</h2>
          </motion.div>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((f, i) => <FAQ key={i} q={f.q} a={f.a} />)}
          </div>
        </motion.section>
      </ProgrammePageShell>
    </>
  );
};

export default LongTermSupport;
